import { useEffect } from "react";
import { Copy, Download } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { copyText } from "@/components/brand/ColourSwatch";
import { useNoIndex } from "@/lib/useNoIndex";
import { SignatureBuilder } from "@/components/site/SignatureBuilder";

const IMAGES = [
  { n: 1, name: "Logo on navy", note: "Navy background built in. Looks the same in light and dark mail apps.", w: 1114, h: 384, show: 260, bg: "#0A1B2E" },
  { n: 2, name: "Logo, white text, transparent", note: "For dark signatures only; the white text disappears on white.", w: 1114, h: 384, show: 260, bg: "#0A1B2E" },
  { n: 3, name: "Icon", note: "Small square mark, e.g. next to your name.", w: 1024, h: 1024, show: 64, bg: "#FFFFFF" },
  { n: 4, name: "Legal logo with ABN and ACN", note: "Navy text on transparent. Best for light signatures.", w: 1465, h: 469, show: 320, bg: "#FFFFFF" },
  { n: 5, name: "Legal logo with ABN only", note: "Navy text on transparent. Best for light signatures.", w: 1452, h: 473, show: 320, bg: "#FFFFFF" },
];

const SigCard = ({ img }) => {
  const url = `${window.location.origin}/signature/aerosoftsolutions${img.n}.png`;
  const height = Math.round((img.show * img.h) / img.w);
  const html = `<img src="${url}" width="${img.show}" height="${height}" alt="Aero Soft Solutions" style="display:block;border:0;">`;
  return (
    <article className="overflow-hidden rounded-[28px] border border-sky-100 bg-white" data-testid={`sig-card-${img.n}`}>
      <div className="flex h-44 items-center justify-center p-6" style={{ background: img.bg }}>
        <img src={url} alt={`Aero Soft Solutions ${img.name}`} className="max-h-full max-w-full object-contain" />
      </div>
      <div className="border-t border-sky-100 p-5">
        <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-sky-700">aerosoftsolutions{img.n}.png · {img.w}×{img.h}</p>
        <h2 className="mt-1 font-display text-lg font-semibold text-ink">{img.name}</h2>
        <p className="mt-1 text-sm text-slate-600">{img.note} Suggested signature width {img.show} px.</p>
        <p className="mt-3 break-all rounded-xl bg-[#F4F9FD] px-3 py-2 font-mono text-[12px] text-ink" data-testid={`sig-url-${img.n}`}>{url}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={() => copyText(url, "Image link")} className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3.5 py-1.5 text-xs font-medium text-white hover:bg-sky-700" data-testid={`sig-copy-url-${img.n}`}><Copy className="h-3.5 w-3.5" />Copy link</button>
          <button onClick={() => copyText(html, "HTML")} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-medium text-ink hover:border-sky-600 hover:text-sky-700" data-testid={`sig-copy-html-${img.n}`}><Copy className="h-3.5 w-3.5" />Copy HTML</button>
          <a href={url} download className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-medium text-ink hover:border-sky-600 hover:text-sky-700" data-testid={`sig-download-${img.n}`}><Download className="h-3.5 w-3.5" />Download</a>
        </div>
      </div>
    </article>
  );
};

export default function EmailSignature() {
  useNoIndex();
  useEffect(() => {
    document.title = "Email signature images | Aero Soft Solutions";
  }, []);
  return (
    <div className="sky-wash min-h-screen" data-testid="signature-page">
      <div className="mx-auto max-w-6xl px-5 py-10 sm:px-6">
        <Logo />
        <p className="mt-12 font-mono text-xs uppercase tracking-[0.25em] text-sky-700">Email signature</p>
        <h1 className="mt-3 font-display text-4xl font-semibold tracking-[-0.03em] text-ink sm:text-5xl lg:text-6xl">Signature images</h1>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-600 md:text-base">Link these from your email signature so the logo loads from the website. In Outlook or Gmail, insert an image "from a web address" and paste the link; in an HTML signature, paste the HTML snippet.</p>
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">{IMAGES.map((img) => <SigCard key={img.n} img={img} />)}</div>
        <SignatureBuilder />
      </div>
    </div>
  );
}
